<template>
   <div class="flex grow items-center justify-center" v-if="!stripeActive">
      <div class="__floating-toast">
         <Toast
            type="error"
            inline
            :title="$t('info.organization-stripe.title')"
            :description="$t('info.organization-stripe.description')"
         />
      </div>
   </div>

   <div v-else class="flex flex-col gap-4 p-4">
      <Toast
         type="info"
         inline
         :title="$t('info.organization-stripe.extra-title')"
         :description="$t('info.organization-stripe.extra-description')"
      />

      <Separator :title="$t('info.organization-stripe.fiscal-report-link')" :showLine="false" />

      <ul v-if="fiscalReports?.data.value?.length" class="__list">
         <li v-for="report in fiscalReports.data.value" :key="report.id">
            <CardOutline>
               <div class="flex flex-col gap-4 p-6 px-8">
                  <div class="flex items-start justify-between">
                     <div class="flex flex-col gap-2">
                        <p class="h-m">{{ report.id }}</p>
                        <div class="flex items-center gap-1">
                           <HighlightedText
                              v-if="report.file_url"
                              type="success"
                              text="XLSX"
                              :uppercase="false"
                           />
                           <HighlightedText
                              v-if="report.stripe_file_url"
                              type="light-blue"
                              text="Stripe CSV"
                              :uppercase="false"
                           />
                           <HighlightedText
                              v-if="!report.file_url && !report.stripe_file_url"
                              type="warning"
                              :text="$t('info.organization-stripe.fiscal-report-no-files')"
                              :uppercase="false"
                           />
                        </div>
                     </div>
                     <div class="flex flex-col gap-2">
                        <p class="p-s text-grey">
                           {{ dayjs(report.date_from).format('DD/MM/YYYY') }} -
                           {{ dayjs(report.date_to).format('DD/MM/YYYY') }}
                        </p>
                        <Button
                           v-if="report.file_url || report.stripe_file_url"
                           variant="light-grey"
                           icon="arrow-down-tray"
                           :loading="downloadingReport === report.id"
                           @click="handleDownloadAll(report)"
                        >
                           {{ $t('info.organization-stripe.fiscal-report-download') }}
                        </Button>
                     </div>
                  </div>
               </div>
            </CardOutline>
         </li>
      </ul>

      <NoData v-else :text="$t('info.organization-stripe.fiscal-report-empty')" />
   </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { useGetSignedFileUrl } from '~/composables/api/helpers/get-signed-file-url'
import { useFiscalReport } from '~/composables/api/queries/fiscal-report'

type Props = {
   stripeActive?: boolean
}
const props = withDefaults(defineProps<Props>(), {})

const organizationId = useOrganizationId()
const config = useRuntimeConfig()

const downloadingReport = ref<string>()
const getSignedFileUrl = useGetSignedFileUrl()

const fiscalReports = useFiscalReport(
   computed(() => ({
      organizationId: organizationId.value || '',
      enabled: !!organizationId.value,
   }))
)

const handleDownloadAll = async (report: {
   id: string
   file_url: string | null
   stripe_file_url: string | null
}) => {
   downloadingReport.value = report.id
   const paths = [report.file_url, report.stripe_file_url].filter(Boolean) as string[]
   for (const path of paths) {
      const url = await getSignedFileUrl.mutateAsync({ path })
      navigateTo(url || '', {
         external: true,
         open: {
            target: '_blank',
         },
      })
   }
   downloadingReport.value = undefined
}

const action = () => {
   window.open(
      `${config.public.url}/redirect/stripe?organizationId=${organizationId.value}`,
      '_blank'
   )
}

defineExpose({
   action: action,
   loading: false,
   disabled: false,
})
</script>
